import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { Add, InfoOutlined, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Box, CircularProgress, Divider, IconButton, Menu, MenuItem, TextField, Tooltip } from '@mui/material';
import { OCR_GUIDE_ROW_HEIGHT, buildOutlinedErrorBorderStyles, mergeOutlinedInputStyles, receiptOutlinedInputStyles } from './AuditDynamicFormConstants';
import { sanitizeVendingSalesIntegerInput } from '../../features/audit/fixOcr/utils/vendingSalesInputUtils';
import auditService from '../../services/auditService';
import type { VendingSalesLine } from '../../types/vendingSalesFormTypes';

type VendingSalesFormLineRowProps = {
  field: any;
  idx: number;
  line?: VendingSalesLine | null;
  control: any;
  rowErrors?: any;
  setValue: (name: string, value: unknown, options?: any) => void;
  remove: (index: number) => void;
  onInsert: (index: number) => void;
  gamesData: Map<string, number> | null;
  reportType: 'vending_sales' | 'vending_inventory';
  queueId?: string;
  reportDate?: string;
  auditStatus?: string;
  hoveredRowIndex: number | null;
  onRowHover?: (index: number | null) => void;
  isNewPack?: boolean;
};

const toOptionalNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const toRequestNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatPackCandidateDate = (value: unknown): string => {
  if (typeof value !== 'string' || !value.trim()) {
    return '-';
  }

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return value;
  }

  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
};

const formatPackCandidateLocation = (candidate: any): string => {
  const location = candidate?.pack_location || candidate?.last_backstock_location;
  if (!location) {
    return '-';
  }

  const normalized = String(location).trim();
  if (!normalized) {
    return '-';
  }

  const backstockState = String(candidate?.backstock_state || '').trim().toLowerCase();
  const backstockLocations = new Set(['cashier', 'locker', 'vending']);
  if (backstockState && backstockState !== 'none' && backstockLocations.has(normalized.toLowerCase())) {
    return `backstock ${normalized}`;
  }

  return normalized;
};

const stepIntegerValue = (value: unknown, delta: number): string => {
  const current = toOptionalNumber(value) ?? 0;
  return String(Math.max(0, current + delta));
};

const VendingSalesFormLineRow: React.FC<VendingSalesFormLineRowProps> = React.memo(({
  field,
  idx,
  line,
  control,
  rowErrors,
  setValue,
  remove,
  onInsert,
  gamesData,
  reportType,
  queueId,
  reportDate,
  auditStatus,
  hoveredRowIndex,
  onRowHover,
  isNewPack,
}) => {

  const isHovered = hoveredRowIndex === idx;
  const isRowHovered = hoveredRowIndex === idx + 1;
  const highlightColor = '#3a3128';

  const hasBinError = !!rowErrors?.bin;
  const hasGameError = !!rowErrors?.game;
  const hasCountError = !!rowErrors?.count || !!rowErrors?.quantity;
  const hasAmountError = !!rowErrors?.amount || !!rowErrors?.value;
  const isInventoryReady = reportType === 'vending_inventory' && String(auditStatus || '').toLowerCase() === 'ready';
  const [packMenuAnchorEl, setPackMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [dynamicPackCandidates, setDynamicPackCandidates] = useState<any[] | null>(null);
  const [packRecommendationsLoading, setPackRecommendationsLoading] = useState(false);
  const [packRecommendationsError, setPackRecommendationsError] = useState<string | null>(null);
  const isPackMenuOpen = Boolean(packMenuAnchorEl);

  const handleRemove = useCallback(() => {
    remove(idx);
  }, [idx, remove]);

  const updateCountValue = useCallback((value: unknown, delta: number) => {
    const fieldName = `parsed_data.final_json.lines.${idx}.${reportType === 'vending_inventory' ? 'quantity' : 'count'}`;
    setValue(fieldName, stepIntegerValue(value, delta), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [idx, reportType, setValue]);

  const inputSx = useMemo(() => receiptOutlinedInputStyles, []);

  const computedAmount = useMemo(() => {
    const gameId = line?.game != null ? String(line.game).trim() : '';
    const costPerTicket = gameId && gamesData ? Number(gamesData.get(gameId) || 0) : 0;
    const countKey = reportType === 'vending_inventory' ? 'quantity' : 'count';
    const count = toOptionalNumber((line as any)?.[countKey]) ?? 0;
    return costPerTicket * count;
  }, [gamesData, line, reportType]);

  useEffect(() => {
    const nextAmount = Number.isFinite(computedAmount) ? Number(computedAmount.toFixed(2)) : 0;
    const valueKey = reportType === 'vending_inventory' ? 'value' : 'amount';
    const currentAmount = toOptionalNumber((line as any)?.[valueKey]);

    if (currentAmount === nextAmount) {
      return;
    }

    setValue(`parsed_data.final_json.lines.${idx}.${valueKey}`, nextAmount, {
      shouldDirty: true,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [computedAmount, idx, line, reportType, setValue]);

  const inventoryGamePackValue = useMemo(() => {
    const gamePart = line?.game === null || line?.game === undefined
      ? ''
      : String(line.game).trim();
    const preferredPackValue = isInventoryReady
      ? (line as any)?.pack
      : ((line as any)?.selected_pack_id ?? (line as any)?.pack);
    const rawPackPart = preferredPackValue === null || preferredPackValue === undefined || preferredPackValue === ''
      ? '-'
      : String(preferredPackValue).trim() || '-';
    const packPart = rawPackPart === '-' ? '' : rawPackPart;
    return gamePart ? `${gamePart}-${packPart}` : '';
  }, [isInventoryReady, line]);

  const packCandidates = useMemo(() => {
    if (dynamicPackCandidates) {
      return dynamicPackCandidates;
    }
    const candidates = (line as any)?.pack_candidates;
    return Array.isArray(candidates) ? candidates : [];
  }, [dynamicPackCandidates, line]);

  useEffect(() => {
    setDynamicPackCandidates(null);
  }, [line?.bin, line?.game, (line as any)?.quantity, (line as any)?.value, reportDate]);

  const updateInventoryGamePack = useCallback((rawValue: string) => {
    const cleanValue = rawValue.replace(/[^\d-]/g, '');
    const parts = cleanValue.split('-');
    const gamePart = (parts[0] || '').replace(/[^\d]/g, '').slice(0, 6);
    const packPart = (parts[1] || '').replace(/[^\d]/g, '').slice(0, 7);

    setValue(`parsed_data.final_json.lines.${idx}.game`, gamePart, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue(`parsed_data.final_json.lines.${idx}.pack`, packPart, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [idx, setValue]);

  const handleOpenPackMenu = useCallback(async (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setPackMenuAnchorEl(event.currentTarget);
    setPackRecommendationsError(null);

    const gameId = toRequestNumber(line?.game);
    if (!queueId || !reportDate || gameId === null || reportType !== 'vending_inventory') {
      return;
    }

    setPackRecommendationsLoading(true);
    try {
      const response = await auditService.getVendingInventoryPackRecommendations(queueId, {
        report_date: reportDate,
        game_id: gameId,
        bin: toRequestNumber(line?.bin),
        quantity: toRequestNumber((line as any)?.quantity),
        value: toRequestNumber((line as any)?.value),
      });
      const nextCandidates = Array.isArray(response.candidates) ? response.candidates : [];
      setDynamicPackCandidates(nextCandidates);
      setValue(`parsed_data.final_json.lines.${idx}.pack_candidates`, nextCandidates, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
      if (((line as any)?.quantity === null || (line as any)?.quantity === undefined || (line as any)?.quantity === '') && Number.isFinite(response.quantity)) {
        setValue(`parsed_data.final_json.lines.${idx}.quantity`, String(response.quantity), {
          shouldDirty: true,
          shouldTouch: false,
          shouldValidate: true,
        });
      }
    } catch (error: any) {
      setPackRecommendationsError(error?.response?.data?.detail || error?.message || 'Could not load pack recommendations');
    } finally {
      setPackRecommendationsLoading(false);
    }
  }, [idx, line, queueId, reportDate, reportType, setValue]);

  const handleClosePackMenu = useCallback(() => {
    setPackMenuAnchorEl(null);
  }, []);

  const handleSelectPack = useCallback((packId: number | null | undefined) => {
    setValue(`parsed_data.final_json.lines.${idx}.pack`, packId == null ? '-' : String(packId), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    handleClosePackMenu();
  }, [handleClosePackMenu, idx, setValue]);

  return (
    <React.Fragment key={`vending-sales-line-${field.id}-${idx}`}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'transparent',
          cursor: 'pointer',
          opacity: 1,
          '&:hover .add-btn': { opacity: 1 },
          height: 0,
          minHeight: 0,
          overflow: 'visible',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          className="add-btn"
          sx={{
            position: 'absolute',
            top: -10,
            left: 0,
            right: 0,
            width: 'calc( 100% - 20px )',
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            opacity: 0,
            transition: 'opacity 0.12s ease-in-out',
          }}
        >
          <IconButton size="small" color="primary" sx={{ p: 0, width: 20, height: 20 }} onClick={() => onInsert(idx)}>
            <Add sx={{ fontSize: 20 }} />
          </IconButton>
          <Divider
            sx={{
              borderColor: isHovered ? highlightColor : '#A3AED099',
              borderWidth: '0.5px',
              borderStyle: 'dashed',
              width: '100%',
              transition: 'all 0.1s ease-in-out',
              boxShadow: isHovered ? `0 0 8px ${highlightColor}40` : 'none',
            }}
          />
        </Box>
      </Box>

      <Box
        onMouseEnter={() => onRowHover?.(idx + 1)}
        onMouseLeave={() => onRowHover?.(null)}
        sx={{
          display: 'grid',
          gridTemplateColumns: reportType === 'vending_inventory'
            ? '64px minmax(196px, 1.08fr) 96px 104px 20px 20px'
            : '72px 1fr 112px 132px 36px',
          alignItems: 'center',
          px: 0,
          py: 0,
          height: OCR_GUIDE_ROW_HEIGHT,
          minHeight: OCR_GUIDE_ROW_HEIGHT,
          maxHeight: OCR_GUIDE_ROW_HEIGHT,
          bgcolor: 'transparent',
          position: 'relative',
          '&:hover .minus-btn': { opacity: 1 },
          columnGap: reportType === 'vending_inventory' ? 0.25 : 1.5,
          borderBottom: isRowHovered ? `2px solid ${highlightColor}` : '1px dashed #d6b982',
          boxShadow: isRowHovered ? `0 0 8px ${highlightColor}40` : 'none',
          fontFamily: 'inherit',
        }}
      >
        <Controller
          name={`parsed_data.final_json.lines.${idx}.bin`}
          control={control}
          render={({ field: binField }) => (
            <Box
              sx={{
                width: '100%',
                textAlign: 'center',
                px: 0,
                py: 0,
                display: 'flex',
                alignItems: 'center',
                height: OCR_GUIDE_ROW_HEIGHT,
                position: 'relative',
              }}
            >
              <TextField
                {...binField}
                value={binField.value ?? ''}
                onChange={(event) => binField.onChange(sanitizeVendingSalesIntegerInput(event.target.value).slice(0, 3))}
                placeholder={reportType === 'vending_inventory' ? '1' : ''}
                error={hasBinError}
                size="small"
                sx={mergeOutlinedInputStyles({
                  ...inputSx,
                  ...buildOutlinedErrorBorderStyles(hasBinError),
                  width: '100%',
                  color: '#222B45',
                  '& .MuiInputBase-input': {
                    ...inputSx['& .MuiInputBase-input'],
                    fontSize: 17,
                    fontFamily: '"Courier Prime", "Courier New", monospace',
                    fontWeight: 400,
                    color: '#222B45',
                    textAlign: 'center',
                  },
                })}
                slotProps={{ inputLabel: { shrink: false } }}
              />
            </Box>
          )}
        />

        <Controller
          name={`parsed_data.final_json.lines.${idx}.game`}
          control={control}
          render={({ field: gameField }) => (
            <Box
              sx={{
                width: '100%',
                textAlign: 'center',
                px: 0,
                py: 0,
                display: 'flex',
                alignItems: 'center',
                height: OCR_GUIDE_ROW_HEIGHT,
                position: 'relative',
              }}
            >
              {reportType === 'vending_inventory' ? (
                <>
                  <TextField
                    {...gameField}
                    value={inventoryGamePackValue}
                    onChange={(event) => {
                      updateInventoryGamePack(event.target.value);
                    }}
                    error={hasGameError}
                    size="small"
                    sx={mergeOutlinedInputStyles({
                      ...inputSx,
                      ...buildOutlinedErrorBorderStyles(hasGameError),
                      width: '100%',
                      color: '#222B45',
                      '& .MuiInputBase-input': {
                        ...inputSx['& .MuiInputBase-input'],
                        fontSize: 17,
                        fontFamily: '"Courier Prime", "Courier New", monospace',
                        fontWeight: 400,
                        color: '#222B45',
                        textAlign: 'left',
                        paddingLeft: '8px !important',
                        paddingRight: '24px !important',
                      },
                    })}
                    slotProps={{ inputLabel: { shrink: false } }}
                  />
                  {isInventoryReady && (
                    <IconButton
                      size="small"
                      onClick={handleOpenPackMenu}
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        p: 0,
                        width: 24,
                        height: 24,
                        minWidth: 24,
                        color: '#2C6CA0',
                        zIndex: 2,
                      }}
                    >
                      <KeyboardArrowDown sx={{ fontSize: 22 }} />
                    </IconButton>
                  )}
                </>
              ) : (
                <TextField
                  {...gameField}
                  value={gameField.value ?? ''}
                  onChange={(event) => gameField.onChange(sanitizeVendingSalesIntegerInput(event.target.value).slice(0, 6))}
                  error={hasGameError}
                  size="small"
                  sx={mergeOutlinedInputStyles({
                    ...inputSx,
                    ...buildOutlinedErrorBorderStyles(hasGameError),
                    width: '100%',
                    color: '#222B45',
                    '& .MuiInputBase-input': {
                      ...inputSx['& .MuiInputBase-input'],
                      fontSize: 17,
                      fontFamily: '"Courier Prime", "Courier New", monospace',
                      fontWeight: 400,
                      color: '#222B45',
                      textAlign: 'center',
                    },
                  })}
                  slotProps={{ inputLabel: { shrink: false } }}
                />
              )}
              {reportType === 'vending_inventory' && isInventoryReady && (
                <>
                  <Menu
                    anchorEl={packMenuAnchorEl}
                    open={isPackMenuOpen}
                    onClose={handleClosePackMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                      paper: {
                        sx: {
                          mt: 0.25,
                          border: '1px solid #d6b982',
                          boxShadow: '0 4px 10px rgba(80, 56, 26, 0.12)',
                          '& .MuiMenuItem-root': {
                            minHeight: 24,
                            fontSize: 14,
                            fontFamily: '"Courier Prime", "Courier New", monospace',
                            py: 0.25,
                            px: 1,
                          },
                        },
                      },
                    }}
                  >
                    {packRecommendationsLoading ? (
                      <MenuItem disabled>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={14} />
                          Loading packs
                        </Box>
                      </MenuItem>
                    ) : packRecommendationsError ? (
                      <MenuItem disabled>
                        {packRecommendationsError}
                      </MenuItem>
                    ) : packCandidates.length > 0 ? packCandidates.map((candidate: any, optionIndex: number) => (
                      <MenuItem
                        key={`pack-candidate-${idx}-${optionIndex}-${candidate?.pack_id ?? 'na'}`}
                        onClick={() => handleSelectPack(candidate?.pack_id)}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                          <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                            {`${candidate?.pack_id ?? '-'} (${formatPackCandidateLocation(candidate)})`}
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              color: '#374151',
                              fontSize: 12,
                              fontWeight: 600,
                              lineHeight: 1.2,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {`${candidate?.status ?? '-'} | Set ${formatPackCandidateDate(candidate?.settled_date ?? candidate?.status_date)} | Act ${formatPackCandidateDate(candidate?.activated_date)} | Rem ${candidate?.latest_remaining_qty ?? '-'}`}
                          </Box>
                        </Box>
                      </MenuItem>
                    )) : (
                      <MenuItem disabled>
                        No packs
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </Box>
          )}
        />

        <Controller
          name={`parsed_data.final_json.lines.${idx}.${reportType === 'vending_inventory' ? 'quantity' : 'count'}`}
          control={control}
          render={({ field: countField }) => (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                px: 0,
                py: 0,
                height: OCR_GUIDE_ROW_HEIGHT,
                gap: 0.25,
              }}
            >
              <TextField
                {...countField}
                value={countField.value ?? ''}
                onChange={(event) => countField.onChange(sanitizeVendingSalesIntegerInput(event.target.value).slice(0, 6))}
                error={hasCountError}
                size="small"
                sx={mergeOutlinedInputStyles({
                  ...inputSx,
                  ...buildOutlinedErrorBorderStyles(hasCountError),
                  width: 'calc(100% - 44px)',
                  flex: '0 0 calc(100% - 44px)',
                  color: '#222B45',
                  '& .MuiInputBase-input': {
                    ...inputSx['& .MuiInputBase-input'],
                    fontSize: 17,
                    fontFamily: '"Courier Prime", "Courier New", monospace',
                    fontWeight: 400,
                    color: '#222B45',
                    textAlign: 'right',
                    paddingRight: '4px !important',
                  },
                })}
                slotProps={{ inputLabel: { shrink: false } }}
              />
              <Box
                className="count-stepper"
                sx={{
                  flex: '0 0 44px',
                  width: 44,
                  height: OCR_GUIDE_ROW_HEIGHT - 2,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: 0.25,
                  opacity: isRowHovered ? 1 : 0,
                  pointerEvents: isRowHovered ? 'auto' : 'none',
                  transition: 'opacity 0.12s ease-in-out',
                  zIndex: 3,
                }}
              >
                <IconButton
                  aria-label="Decrease count"
                  size="small"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(event) => {
                    event.stopPropagation();
                    updateCountValue(countField.value, -1);
                  }}
                  sx={{
                    p: 0,
                    width: 22,
                    height: 22,
                    minWidth: 22,
                    bgcolor: 'rgba(255, 250, 240, 0.95)',
                    border: '1px solid #b98542',
                    borderRadius: 0.75,
                    color: '#2b2825',
                    '&:hover': { bgcolor: '#f4e6cc' },
                  }}
                >
                  <KeyboardArrowDown sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  aria-label="Increase count"
                  size="small"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(event) => {
                    event.stopPropagation();
                    updateCountValue(countField.value, 1);
                  }}
                  sx={{
                    p: 0,
                    width: 22,
                    height: 22,
                    minWidth: 22,
                    bgcolor: 'rgba(255, 250, 240, 0.95)',
                    border: '1px solid #b98542',
                    borderRadius: 0.75,
                    color: '#2b2825',
                    '&:hover': { bgcolor: '#f4e6cc' },
                  }}
                >
                  <KeyboardArrowUp sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>
          )}
        />

        <Controller
          name={`parsed_data.final_json.lines.${idx}.${reportType === 'vending_inventory' ? 'value' : 'amount'}`}
          control={control}
          render={({ field: amountField }) => (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                px: 0,
                py: 0,
                height: OCR_GUIDE_ROW_HEIGHT,
              }}
            >
              <TextField
                {...amountField}
                value={(Number.isFinite(computedAmount) ? computedAmount.toFixed(2) : '0.00')}
                placeholder="0.00"
                error={hasAmountError}
                size="small"
                disabled
                sx={mergeOutlinedInputStyles({
                  ...inputSx,
                  ...buildOutlinedErrorBorderStyles(hasAmountError),
                  width: '100%',
                  color: '#222B45',
                  '& .MuiInputBase-input': {
                    ...inputSx['& .MuiInputBase-input'],
                    fontSize: 17,
                    fontFamily: '"Courier Prime", "Courier New", monospace',
                    fontWeight: 400,
                    color: '#222B45',
                    textAlign: 'right',
                    WebkitTextFillColor: '#222B45',
                  },
                })}
                slotProps={{ inputLabel: { shrink: false } }}
              />
            </Box>
          )}
        />

        {reportType === 'vending_inventory' && (
          <Box
            sx={{
              height: OCR_GUIDE_ROW_HEIGHT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 0.5,
            }}
          >
            {isNewPack && (
              <Tooltip title="New pack: not present in previous inventory" arrow placement="top">
                <Box
                  component="span"
                  sx={{
                    width: 20,
                    height: 20,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#2C6CA0',
                  }}
                >
                  <InfoOutlined sx={{ fontSize: 18 }} />
                </Box>
              </Tooltip>
            )}
          </Box>
        )}

        <Box
          sx={{
            minWidth: 24,
            width: 24,
            maxWidth: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 0,
          }}
        >
          <IconButton
            className="minus-btn"
            onClick={handleRemove}
            size="small"
            sx={{
              opacity: isRowHovered ? 1 : 0,
              transition: 'opacity 0.2s',
              color: 'rgba(34, 43, 69, 1)',
              '&:hover': { opacity: 1 },
              '&.Mui-disabled': { opacity: 0.3 },
              padding: 0,
              width: '17px',
              height: '17px',
              minWidth: '17px',
              minHeight: '17px',
              maxWidth: '17px',
              maxHeight: '17px',
            }}
          >
            <span style={{ fontSize: 16, fontWeight: 700, lineHeight: '17px', height: '17px' }}>
              -
            </span>
          </IconButton>
        </Box>
      </Box>
    </React.Fragment>
  );
});

VendingSalesFormLineRow.displayName = 'VendingSalesFormLineRow';

export default VendingSalesFormLineRow;
